import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CONNECT_UI_URL } from '../config';

const ICON_PATHS = {
  back: 'M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18',
  chart: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z',
  folder: 'M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z',
  sparkles: 'M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z',
  users: 'M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z',
  chevron: 'm19.5 8.25-7.5 7.5-7.5-7.5',
  external: 'M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25'
};

const NAV_GROUPS = [
  {
    key: 'general',
    label: 'General',
    icon: 'chart',
    items: [
      { to: '/admin/statistics', label: 'Statistics' },
      { to: '/admin/analytics', label: 'Traffic & usage' },
      { to: '/admin/data-health', label: 'Data health' },
      { to: '/admin/indexation', label: 'Indexation' },
      { to: '/admin/search-insights', label: 'Search performance', superadminOnly: true }
    ]
  },
  {
    key: 'project',
    label: 'By project',
    icon: 'folder',
    items: [
      { to: '/admin/questions', label: 'Questions settings' },
      { to: '/admin/sectors', label: 'Sectors settings' },
      { to: '/admin/duplicates', label: 'Duplicates Detector™' }
    ]
  },
  {
    key: 'ai',
    label: 'AI analysis',
    icon: 'sparkles',
    superadminOnly: true,
    items: [
      { to: '/admin/ai/agents', label: 'AI agents' },
      { to: '/admin/ai/configurations', label: 'Configurations' }
    ]
  }
];

const covers = (pathname, path) => pathname === path || pathname.startsWith(`${path}/`);

const Icon = ({ name, className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d={ICON_PATHS[name]} />
  </svg>
);

const rowClass = (highlighted) => [
  'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-sm leading-tight transition-colors',
  highlighted
    ? 'bg-blue-50 font-medium text-blue-700 dark:bg-blue-950/60 dark:text-blue-200'
    : 'text-gray-700 hover:bg-gray-200/70 dark:text-gray-300 dark:hover:bg-gray-700/60'
].join(' ');

/**
 * Back-office navigation, as collapsible groups.
 *
 * The flat list it replaces spelled out every destination at once behind uppercase headings, which
 * made the sidebar taller than the viewport and gave the page a scrollbar of its own. Only the
 * section being worked in is unfolded; the others cost one row each.
 */
const AdminSidebar = ({ currentUser, pathname, onNavigate }) => {
  const { t } = useTranslation();

  const groups = useMemo(
    () => NAV_GROUPS
      .filter(group => !group.superadminOnly || currentUser.issuperadmin)
      .map(group => ({
        ...group,
        items: group.items.filter(item => !item.superadminOnly || currentUser.issuperadmin)
      })),
    [currentUser.issuperadmin]
  );

  const activeGroupKey = useMemo(
    () => groups.find(group => group.items.some(item => covers(pathname, item.to)))?.key,
    [groups, pathname]
  );

  const [openKeys, setOpenKeys] = useState(() => activeGroupKey ? [activeGroupKey] : []);

  // Navigating into a folded section unfolds it, without refolding the ones opened by hand.
  useEffect(() => {
    if (!activeGroupKey) return;
    setOpenKeys(keys => keys.includes(activeGroupKey) ? keys : [...keys, activeGroupKey]);
  }, [activeGroupKey]);

  const toggle = (key) => setOpenKeys(keys => keys.includes(key) ? keys.filter(entry => entry !== key) : [...keys, key]);

  return (
    <nav className="flex h-full flex-col gap-1 overflow-y-auto p-3">
      <Link to="/gallery" onClick={onNavigate} className={rowClass(false)}>
        <Icon name="back" className="h-4 w-4 shrink-0" />
        {t('Back to Gallery')}
      </Link>

      <div className="my-1 border-t border-gray-200 dark:border-gray-700" />

      <ul className="space-y-0.5">
        {groups.map(group => {
          const open = openKeys.includes(group.key);
          return (
            <li key={group.key}>
              <button type="button" onClick={() => toggle(group.key)} aria-expanded={open} className={rowClass(activeGroupKey === group.key)}>
                <Icon name={group.icon} className="h-4 w-4 shrink-0" />
                <span className="flex-1">{t(group.label)}</span>
                <Icon name="chevron" className={`h-3.5 w-3.5 shrink-0 opacity-60 transition-transform ${open ? '' : '-rotate-90'}`} />
              </button>

              {open && (
                <ul className="ml-[1.125rem] mt-0.5 space-y-0.5 border-l border-gray-200 pl-2.5 dark:border-gray-700">
                  {group.items.map(item => (
                    <li key={item.to}>
                      <Link to={item.to} onClick={onNavigate} className={rowClass(covers(pathname, item.to))}>
                        {t(item.label)}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      {currentUser.issuperadmin && (
        <a
          href={`${CONNECT_UI_URL}/admin`}
          target="_blank"
          rel="noopener noreferrer"
          className={`mt-auto ${rowClass(false)}`}
        >
          <Icon name="users" className="h-4 w-4 shrink-0" />
          <span className="flex-1">{t('User management')}</span>
          <Icon name="external" className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </a>
      )}
    </nav>
  );
};

export default AdminSidebar;
